import { useParams, Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, Clock, Euro, MapPin, ArrowLeft, Ticket, Sparkles, Music, ArrowRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { getEventBySlug, transformEventForDisplay, type Event } from '../services/eventsService'
import { getEventImageUrl, EVENT_IMAGE_FALLBACK } from '../config/api'

export default function EventDetail() {
  const { eventSlug } = useParams<{ eventSlug: string }>()
  const [event, setEvent] = useState<Event | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadEvent() {
      if (!eventSlug) {
        setLoading(false)
        return
      }

      try {
        const loadedEvent = await getEventBySlug(eventSlug)
        setEvent(loadedEvent)
      } catch (error) {
        console.error('Fehler beim Laden des Events:', error)
      } finally {
        setLoading(false)
      }
    }
    loadEvent()
  }, [eventSlug])

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brand-400"></div>
          <p className="mt-4 text-white/40">Event wird geladen...</p>
        </div>
      </div>
    )
  }

  if (!event) {
    return <Navigate to="/partys" replace />
  }

  const displayEvent = transformEventForDisplay(event)

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HEADER SECTION - Compact with Hero Image
      ════════════════════════════════════════════════════════════ */}
      <section className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden pt-28 pb-12 border-b border-white/5">
        {/* Image Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Event bei Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        </div>
        {/* Background Glow */}
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/10 rounded-full blur-[120px] z-0" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10 h-full flex flex-col justify-center">
          {/* Back Navigation */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-8"
          >
            <Link
              to="/partys"
              className="group inline-flex items-center gap-3 text-white/70 hover:text-white transition-colors duration-300"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 group-hover:bg-white/10 group-hover:border-white/20 transition-all duration-300">
                <ArrowLeft className="h-4 w-4" />
              </div>
              <span className="text-sm uppercase tracking-[0.15em] font-medium">Alle Events</span>
            </Link>
          </motion.div>

          {/* Header Content */}
          <div className="flex flex-col items-center text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mb-6"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
                <Music className="w-3 h-3 text-brand-400" />
                <span className="text-white/70 text-xs uppercase tracking-[0.25em]">{displayEvent.date}</span>
              </div>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
            >
              {displayEvent.title}
            </motion.h1>

            {/* Quick Info Badges */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {displayEvent.time && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-800/20 backdrop-blur-xl border border-brand-800/30 rounded-full">
                  <Clock className="w-4 h-4 text-brand-400" />
                  <span className="text-white text-sm">{displayEvent.time}</span>
                </div>
              )}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
                <Euro className="w-4 h-4 text-white/60" />
                <span className="text-white/70 text-sm">{displayEvent.price}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTENT SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24">
        {/* Background Glows */}
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-40 left-10 w-[400px] h-[400px] bg-brand-900/5 rounded-full blur-[100px]" />
        
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            
            {/* Left: Flyer Image */}
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="relative overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
                <img
                  src={getEventImageUrl(displayEvent.flyerImageUrl || displayEvent.imageUrl)}
                  alt={displayEvent.title}
                  className="w-full h-auto object-cover"
                  onError={(e) => {
                    if ((e.currentTarget as HTMLImageElement).dataset.flyerFallback === '1') return
                    ;(e.currentTarget as HTMLImageElement).dataset.flyerFallback = '1'
                    e.currentTarget.src = EVENT_IMAGE_FALLBACK
                  }}
                />
                {/* Subtle Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
              </div>
            </motion.div>

            {/* Right: Sidebar */}
            <motion.div 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <div className="sticky top-28 space-y-6">
                
                {/* Event Details Card */}
                <div className="p-8 bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 rounded-2xl">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-brand-400" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white">Event-Details</h3>
                      <p className="text-white/40 text-sm">Alle Informationen</p>
                    </div>
                  </div>
                  
                  <div className="h-px bg-white/10 my-6" />
                  
                  {/* Details List */}
                  <div className="space-y-5">
                    {/* Datum */}
                    <div className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Calendar className="h-4 w-4 text-brand-400" />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Datum</p>
                        <p className="text-white font-medium">{displayEvent.fullDate}</p>
                      </div>
                    </div>

                    {/* Uhrzeit */}
                    {displayEvent.time && (
                      <div className="flex items-start gap-4 group">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <Clock className="h-4 w-4 text-brand-400" />
                        </div>
                        <div>
                          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Uhrzeit</p>
                          <p className="text-white font-medium">{displayEvent.time}</p>
                        </div>
                      </div>
                    )}

                    {/* Preis */}
                    <div className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Euro className="h-4 w-4 text-brand-400" />
                      </div>
                      <div>
                        <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Eintritt</p>
                        <p className="text-white font-medium">{displayEvent.price}</p>
                      </div>
                    </div>

                    {/* Location */}
                    {displayEvent.location && (
                      <div className="flex items-start gap-4 group">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-800/10 border border-brand-800/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <MapPin className="h-4 w-4 text-brand-400" />
                        </div>
                        <div>
                          <p className="text-white/40 text-xs uppercase tracking-wider mb-1">Ort</p>
                          <p className="text-white font-medium">{displayEvent.location}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* CTA Button */}
                <motion.a
                  href="https://hueckels-may.ticket.io/?lang=de"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="block w-full px-8 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 text-center"
                >
                  <span className="flex items-center justify-center gap-3">
                    <Ticket className="w-4 h-4" />
                    Jetzt Tickets sichern
                  </span>
                </motion.a>

                <p className="text-center text-white/30 text-xs">
                  Weiterleitung zu ticket.io
                </p>

                {/* Description Card */}
                <div className="p-8 bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 rounded-2xl">
                  <h3 className="font-serif text-xl text-white mb-4">
                    Über das Event
                  </h3>
                  <div className="h-px bg-white/10 mb-6" />
                  
                  <div className="space-y-4">
                    {displayEvent.fullDescription.split('\n\n').map((paragraph, index) => (
                      <p
                        key={index}
                        className="text-white/60 text-sm leading-relaxed"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Mehr entdecken</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Weitere <span className="italic text-white/60">Events</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Entdecken Sie alle kommenden Veranstaltungen bei Hückels May.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/partys">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 bg-white text-black text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-3"
                >
                  Alle Events
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-10 py-4 border border-white/30 text-white text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-3"
                >
                  Kontakt
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  )
}
